import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MonitorPlay, Copy, Trash2, Clock, Plus, ExternalLink } from "lucide-react";

const fullLink = (path) => `${window.location.origin}${path}`;
const DEMO_LOGIN = "/login?demo=1";

function countdown(expires_at) {
  const ms = new Date(expires_at).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return `${d}d ${h}h left`;
}

export default function AdminDemoEnvironments() {
  const [rows, setRows] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/demo/instances").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo/instances", { label });
      setLabel("");
      await load();
      const creds = (data.accounts || []).map((a) => `${a.role}: ${a.email}`).join("\n");
      await navigator.clipboard?.writeText(`${fullLink(DEMO_LOGIN)}\nPassword (all 3): ${data.password}\n${creds}`).catch(() => {});
      toast.success("Demo created — login + 3 credentials copied to clipboard");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create demo"); }
    finally { setBusy(false); }
  };

  const copyCreds = async (r) => {
    const creds = (r.accounts || []).map((a) => `${a.role}: ${a.email}`).join("\n");
    await navigator.clipboard?.writeText(`${fullLink(DEMO_LOGIN)}\nPassword (all 3): ${r.password}\n${creds}`);
    toast.success("Login + credentials copied");
  };
  const teardown = async (id) => {
    if (!window.confirm("Tear down this demo now? Its data is permanently deleted.")) return;
    try { await api.delete(`/admin/demo/instances/${id}`); await load(); toast.success("Demo torn down"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const active = (rows || []).filter((r) => r.status === "active");

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="admin-demo-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100"><MonitorPlay className="h-6 w-6 text-indigo-700" /></div>
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Demo Environments</h2>
          <p className="text-slate-500">Generate isolated 14-day sandboxes for prospects. Each link explores Admin, Client & Provider — fully mocked, no real data or payments.</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="demo-generate-card">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">New demo environment</label>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <Input data-testid="demo-label-input" placeholder="Label (e.g. Founding member — Acme Law)" value={label} onChange={(e) => setLabel(e.target.value)} className="flex-1" />
          <Button data-testid="demo-generate-btn" disabled={busy} onClick={generate} className="bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus className="mr-1.5 h-4 w-4" /> {busy ? "Generating…" : "Generate Demo Environment"}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-semibold text-slate-700">Active demos ({active.length})</p>
        {rows === null && <p className="text-sm text-slate-400">Loading…</p>}
        {rows && active.length === 0 && <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400" data-testid="demo-empty">No active demos. Generate one above.</p>}
        {active.map((r) => (
          <div key={r.id} data-testid={`demo-row-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900">{r.label}</p>
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> Created {new Date(r.created_at).toLocaleString()} · <span className="font-semibold text-amber-600">{countdown(r.expires_at)}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" data-testid={`demo-copy-${r.id}`} onClick={() => copyCreds(r)} className="bg-slate-900 text-white hover:bg-slate-800"><Copy className="mr-1 h-3.5 w-3.5" /> Copy logins</Button>
                <Button size="sm" variant="outline" data-testid={`demo-teardown-${r.id}`} onClick={() => teardown(r.id)} className="border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <div className="mt-2 space-y-1 rounded bg-slate-50 p-2 text-xs" data-testid={`demo-creds-${r.id}`}>
              <p className="text-slate-500">Login at <span className="font-mono text-slate-700">{fullLink(DEMO_LOGIN)}</span> · password <span className="font-mono font-semibold text-slate-700">{r.password}</span></p>
              {(r.accounts || []).map((a) => (
                <p key={a.role} className="font-mono text-slate-600"><span className="inline-block w-16 font-semibold capitalize text-indigo-600">{a.role}</span> {a.email}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
